/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IconButton } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { DropdownMenuItemType, IDropdownOption, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { SavingState } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logDTButtonClick, logDTLanguageDropDownSelect, logDTLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getDTActiveStepName } from 'src/core/utils/telemetryUtils';
import { TranslatorLanguage } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { LanguageInfo, TranslationCreationSteps, TranslationInfoDto } from 'src/translation/components/TranslationCreationPage';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';

export type TranslationBasicInfoPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    isReturningUser: boolean;
    activeStep: TranslationCreationSteps;
    supportedLanguages: TranslatorLanguage[];
    translationConfigsDto: TranslationInfoDto;
    selectedStorageAccount: string;
    onChangeSavingState: (savingState: SavingState) => void;
    onChangeSourceLanguage: (sourceLanguage: LanguageInfo) => void;
    onChangeTargetLanguages: (targetLanguages: LanguageInfo[]) => void;
    onNext: (sourceLanguage: LanguageInfo, targetLanguages: LanguageInfo[]) => void;
};

const messages = defineMessages({
    pageTitle: { id: 'translation.translationBasicInfoPage.pageTitle', defaultMessage: 'Add the basic information for your job' },
    pageSubtitle: {
        id: 'translation.translationBasicInfoPage.pageSubtitle',
        defaultMessage: 'Select the languages for your translation job'
    },
    languagesSectionTitle: { id: 'translation.translationBasicInfoPage.languagesSectionTitle', defaultMessage: 'Languages' },
    languagesSectionSubTitle: {
        id: 'translation.translationBasicInfoPage.languagesSectionSubTitle',
        defaultMessage:
            'Select the language you are translating from and the language(s) you want to translate into. There is a maximum of 10 target languages per job.'
    },
    translateFrom: { id: 'translation.translationBasicInfoPage.translateFrom', defaultMessage: 'Translate from' },
    translateTo: { id: 'translation.translationBasicInfoPage.translateTo', defaultMessage: 'Translate to (max 10)' },
    translateFromPlaceholder: {
        id: 'translation.translationBasicInfoPage.translateFromPlaceholder',
        defaultMessage: 'Select a language to translate from'
    },
    translateToPlaceholder: {
        id: 'translation.translationBasicInfoPage.translateToPlaceholder',
        defaultMessage: 'Select a language(s) to translate to'
    },
    autoDetectLanguageItem: { id: 'translation.translationBasicInfoPage.autoDetectLanguageItem', defaultMessage: 'Auto-detect language' },
    configurationsTitle: { id: 'translation.translationBasicInfoPage.configurationsTitle', defaultMessage: 'Translation configurations' },
    configurationsSubTitle: {
        id: 'translation.translationBasicInfoPage.configurationsSubTitle',
        defaultMessage: 'These configurations are set for all your translation jobs. To change these configurations, edit them in the'
    },
    configurationsSubTitleLink: {
        id: 'translation.translationBasicInfoPage.configurationsSubTitleLink',
        defaultMessage: 'Translation settings'
    },
    resourceTitle: { id: 'translation.translationBasicInfoPage.resourceTitle', defaultMessage: 'Translator resource' },
    blobStorageTitle: { id: 'translation.translationBasicInfoPage.blobStorageTitle', defaultMessage: 'Blob storage account' },
    switchSourceWithDestination: {
        id: 'translation.translationBasicInfoPage.switchSourceWithDestination',
        defaultMessage: 'Switch source language with destination language'
    },
    switchDoneSourceWithDestination: {
        id: 'translation.translationBasicInfoPage.switchDoneSourceWithDestination',
        defaultMessage: 'Switch successful'
    }
});

const languageDropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({ root: { flex: 1, maxWidth: '300px' } });
const calloutStyle = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

export const TranslationBasicInfoPage = observer((props: TranslationBasicInfoPageProps) => {
    const {
        onNext,
        onSubmit,
        onDismiss,
        onPrevious,
        activeStep,
        isReturningUser,
        supportedLanguages,
        onChangeSavingState,
        translationConfigsDto,
        onChangeSourceLanguage,
        selectedStorageAccount,
        onChangeTargetLanguages
    } = props;
    const { localize } = useLocalization();
    const history = useHistory();
    const { rootStore } = useGlobalState();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { announcedComponent, announceAction } = useAnnounced();

    const { csAccountStore } = rootStore.azureStore.session;

    const getLanguageOptions = (isTarget: boolean = false) => [
        ...(!isTarget
            ? [
                  { key: LuisConstants.TranslationTool.AutoLanguageDetectionItemKey, text: localize(messages.autoDetectLanguageItem) },
                  { key: 'divider', text: '', itemType: DropdownMenuItemType.Divider }
              ]
            : []),
        ...supportedLanguages.map(l => ({ key: l.code, text: l.name, disabled: false })).sort((a, b) => (a.text < b.text ? -1 : 1))
    ];

    const targetLanguagesOptions = React.useMemo(() => {
        let languagesOptions = getLanguageOptions(true).filter(l => l.key !== translationConfigsDto.sourceLanguage?.key);
        if (translationConfigsDto.targetLanguages.length >= LuisConstants.TranslationTool.MaxTargetLanguageSelection) {
            languagesOptions = languagesOptions.map(lang => ({
                ...lang,
                disabled: !translationConfigsDto.targetLanguages.map(l => l.key).includes(lang.key)
            }));
        }

        return languagesOptions;
    }, [supportedLanguages, translationConfigsDto.targetLanguages.length, translationConfigsDto.sourceLanguage?.key]);

    const handleTargetLanguageChange = (option: IDropdownOption) => {
        onChangeTargetLanguages(
            option.selected
                ? [...translationConfigsDto.targetLanguages, { key: option.key as string, value: option.text as string }]
                : translationConfigsDto.targetLanguages.filter(t => t.key !== option.key)
        );
        onChangeSavingState('dirty');
        logDTLanguageDropDownSelect('document translation', 'jobCreation', 'translateTo', option.text, 'basicInfo');
    };

    const onConfigurationsSubTitleLinkClick = () => {
        history.push(routeTable.translation.documentTranslationSettings());
        logDTLinkClick('document translation', 'jobCreation', 'translationSettingClick', getDTActiveStepName(activeStep));
    };

    const switchLanguages = () => {
        const tempSourceLang = translationConfigsDto.sourceLanguage;
        onChangeSourceLanguage(translationConfigsDto.targetLanguages[0]);
        onChangeTargetLanguages([tempSourceLang]);
        announceAction(localize(messages.switchDoneSourceWithDestination));
        logDTButtonClick('document translation', 'jobCreation', 'Button', 'switchLanguages', 'basicInfo');
    };

    return (
        <Stack verticalFill verticalAlign="space-between" styles={{ root: { overflow: 'auto' } }}>
            <Stack
                tokens={{ childrenGap: 16, padding: '0 32px' }}
                styles={{ root: { marginBottom: '10px', overflow: 'auto' } }}
                verticalFill>
                <Stack tokens={{ childrenGap: 13 }}>
                    <LuisText variant="xLarge" fontWeight={600}>
                        {localize(messages.pageTitle)}
                    </LuisText>
                    <LuisText variant="medium">{localize(messages.pageSubtitle)}</LuisText>
                </Stack>
                <Stack tokens={{ childrenGap: 13 }}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.languagesSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">{localize(messages.languagesSectionSubTitle)}</LuisText>
                    </Stack>

                    <Stack horizontal={isXLargeOrGreater} verticalAlign="end" tokens={{ childrenGap: 10 }} styles={{ root: { flex: 1 } }}>
                        <LuisDropdown
                            required
                            orderByKey="none"
                            options={getLanguageOptions()}
                            styles={languageDropdownStyles}
                            selectedKey={translationConfigsDto.sourceLanguage?.key}
                            calloutProps={{ styles: calloutStyle }}
                            label={localize(messages.translateFrom)}
                            data-automation-id="sourceLanguageDropdown"
                            ariaLabel={localize(messages.translateFromPlaceholder)}
                            placeholder={localize(messages.translateFromPlaceholder)}
                            onChange={(_, option) => {
                                onChangeSourceLanguage({ key: option.key as string, value: option.text as string });
                                onChangeTargetLanguages(
                                    translationConfigsDto.targetLanguages.filter(l => l.key !== (option.key as string))
                                );
                                onChangeSavingState('dirty');
                                logDTLanguageDropDownSelect(
                                    'document translation',
                                    'jobCreation',
                                    'translateFrom',
                                    option.text,
                                    'basicInfo'
                                );
                            }}
                        />
                        <Stack aria-live="polite">
                            <IconButton
                                iconProps={{ iconName: 'Switch' }}
                                styles={{ root: { lineHeight: '32px' } }}
                                disabled={
                                    translationConfigsDto.targetLanguages.length !== 1 ||
                                    translationConfigsDto.sourceLanguage.key === null ||
                                    translationConfigsDto.sourceLanguage.key === 'autoLanguageDetectionItemKey'
                                }
                                onClick={switchLanguages}
                                ariaLabel={localize(messages.switchSourceWithDestination)}
                            />
                        </Stack>
                        <LuisDropdown
                            required
                            multiSelect
                            styles={languageDropdownStyles}
                            options={targetLanguagesOptions}
                            label={localize(messages.translateTo)}
                            calloutProps={{ styles: calloutStyle }}
                            data-automation-id="targetLanguageDropdown"
                            selectedKeys={translationConfigsDto.targetLanguages.map(l => l.key)}
                            ariaLabel={localize(messages.translateToPlaceholder)}
                            placeholder={localize(messages.translateToPlaceholder)}
                            onChange={(_, option) => handleTargetLanguageChange(option)}
                        />
                    </Stack>
                </Stack>

                <Stack tokens={{ childrenGap: 14 }}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.configurationsTitle)}
                        </LuisText>
                        <LuisText variant="smallPlus">
                            {localize(messages.configurationsSubTitle)}&nbsp;
                            <Link onClick={onConfigurationsSubTitleLinkClick}>{localize(messages.configurationsSubTitleLink)}</Link>
                        </LuisText>
                    </Stack>

                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisText variant="medium">
                            {localize(messages.resourceTitle)}: &nbsp;
                            {csAccountStore?.name}
                        </LuisText>
                        <LuisText variant="medium">
                            {localize(messages.blobStorageTitle)}: &nbsp;
                            {selectedStorageAccount}
                        </LuisText>
                    </Stack>
                </Stack>
            </Stack>

            {announcedComponent}

            <TranslationToolFooter
                onSubmit={onSubmit}
                activeStep={activeStep}
                onPrevious={onPrevious}
                onDismiss={onDismiss}
                isReturningUser={isReturningUser}
                onNext={() => onNext(translationConfigsDto.sourceLanguage, translationConfigsDto.targetLanguages)}
                isNextDisabled={!translationConfigsDto.sourceLanguage || !translationConfigsDto.targetLanguages.length}
                isDiscardDisabled={!translationConfigsDto.sourceLanguage?.key && !translationConfigsDto.targetLanguages.length}
            />
        </Stack>
    );
});
